'use client';

import { useEffect, useRef } from 'react';
import { trackSectionView, trackTimeSpent } from '@/lib/analytics';

/**
 * Hook to detect when an element comes into view and track it
 * Tracks the section view event once when element is first visible
 *
 * Usage:
 *   const ref = useScrollDetection('hero_section');
 *   <div ref={ref}>Content</div>
 */
export function useScrollDetection(sectionName, options = {}) {
    const ref = useRef(null);
    const hasTrackedRef = useRef(false);

    const {
        threshold = 0.5, // Element must be 50% visible
        trackOnceOnly = true, // Only track the first time it comes into view
        analyticsParams = {}, // Additional analytics params
    } = options;

    useEffect(() => {
        if (!ref.current || typeof window === 'undefined') return;

        // Check for IntersectionObserver support (all modern browsers support it)
        // Older browsers (IE 11) will skip scroll tracking silently
        if (!('IntersectionObserver' in window)) {
            // Only warn in development to avoid console pollution in production
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.warn(
                    `IntersectionObserver not supported, scroll tracking for "${sectionName}" disabled (browser too old)`,
                );
            }
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Track only once if trackOnceOnly is true
                    if (trackOnceOnly && hasTrackedRef.current) {
                        return;
                    }

                    // Track the section view
                    trackSectionView(sectionName, {
                        threshold_met: true,
                        ...analyticsParams,
                    });

                    hasTrackedRef.current = true;
                }
            },
            { threshold },
        );

        observer.observe(ref.current);

        return () => {
            observer.disconnect();
        };
    }, [sectionName, threshold, trackOnceOnly, analyticsParams]);

    return ref;
}

/**
 * Hook to track time spent on an element before it unmounts or user leaves
 * Reserved for future use in component-level time tracking
 * Currently, trackTimeSpent() is called directly in useEffect cleanup
 *
 * Usage:
 *   useTimeTracking('product_details', { product_id: '123' });
 *
 * @future Consider using this hook in detail/product pages for automatic time tracking
 */
export function useTimeTracking(section, params = {}) {
    useEffect(() => {
        const startTime = Date.now();

        return () => {
            // On unmount, track time spent
            const timeSpent = (Date.now() - startTime) / 1000;
            if (timeSpent > 1) {
                // Only track if more than 1 second
                trackTimeSpent(section, timeSpent, params);
            }
        };
    }, [section, params]);
}

export default useScrollDetection;
